import cx from 'classnames'
import React, { CSSProperties } from 'react'
import styles from './Accordion.module.scss'
import { Pane, PaneProps } from './Pane'
import { ChevronDown, ChevronRight } from 'almanac-ui/icons'

const animationSpeed = 250

export type AccordionProps = {
  children: React.ReactElement<PaneProps> | React.ReactElement<PaneProps>[]
  expanded: number | undefined
  onHeaderClick: (i: number) => () => void
  allowCollapsingAll?: boolean
  animationActive: ReturnType<typeof setTimeout> | undefined
  className?: string
}

const Chevron = ({ expanded = false }: { expanded: boolean }) => {
  if (expanded) {
    return <ChevronDown color="var(--neutral-40)" />
  }
  return <ChevronRight color="var(--neutral-40)" />
}

const _Accordion: React.FC<React.PropsWithChildren<AccordionProps>> = ({
  children,
  onHeaderClick,
  expanded,
  allowCollapsingAll = false,
  animationActive,
  className,
}) => {
  return (
    <div
      className={cx(
        styles.Accordion,
        className,
        {
          [styles.allowCollapsingAll]: allowCollapsingAll,
        },
        animationActive ? styles.animationActive : styles.animationInactive,
      )}
      style={{ '--animation-speed': `${animationSpeed}ms` } as CSSProperties}
    >
      {React.Children.map(children, (_child, i) => {
        const child = _child as React.ReactElement<PaneProps>
        const wrapperClassName =
          typeof child.props.className === 'string' ? child.props.className : child.props.className?.wrapper
        const headerClassName = typeof child.props.className !== 'string' ? child.props.className?.header : undefined
        const contentClassName = typeof child.props.className !== 'string' ? child.props.className?.content : undefined
        const AccordionChevron = child.props.chevron || Chevron

        return (
          <div key={`pane-${i}`} className={cx(styles.pane, wrapperClassName)} aria-expanded={expanded === i}>
            <div
              className={cx(styles.header, headerClassName)}
              onClick={onHeaderClick(i)}
              onKeyDown={(e) => ['Enter', ' '].includes(e.key) && onHeaderClick(i)()}
              tabIndex={0}
            >
              {child.props.title}
              <AccordionChevron expanded={expanded === i} />
            </div>
            <div className={styles.contentWrapper}>
              <div className={cx(styles.content, contentClassName)}>{child.props.children}</div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export const Accordion = Object.assign(_Accordion, { Pane })
